import React from 'react';
import PropTypes from 'prop-types';

import {merge} from 'lodash';
import {motion} from 'motion/react';
import {useTheme} from '@salesforce/retail-react-app/app/components/shared/ui';
import {jsxParser} from '@amblique/base/app/helpers/jsx-parser';

/**
 * @typedef {'fade-up-text' | 'highlight-rich-text'} VariantType
 */

const fadeUpConfig = {
  initial: {opacity: 0, y: 20},
  animate: {opacity: 1, y: 0},
  transition: {
    duration: 0.65,
    ease: [0.25, 0.1, 0.25, 1],
  },
  style: {display: 'inline-block'},
};

const isBrowser = typeof window !== 'undefined' && typeof DOMParser !== 'undefined';

const createHighlightConfig = (theme, staggerDelay, delay = 0) => ({
  containerVariants: {
    initial: {},
    animate: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay,
      },
    },
  },
  wordVariants: {
    initial: {
      color: theme.colors.brandColors.paleGrey,
    },
    animate: {
      color: theme.colors.brandColors.primaryText,
      transition: {
        duration: 0.15,
        ease: 'easeInOut',
      },
    },
  },
});

const parseHTML = (html) => {
  if (!isBrowser) {
    return null;
  }

  const parser = new DOMParser();

  return parser.parseFromString(html, 'text/html');
};

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

function processHighlightNode(node, wordVariants, wordIndex) {
  if (node.nodeType === TEXT_NODE) {
    return node.textContent.split(/(\s+)/).map((chunk, chunkIndex) => {
      if (chunk.trim() === '') {
        return (
          <span key={`space-${wordIndex.current}-${chunkIndex}`} style={{whiteSpace: 'pre-wrap'}}>
            {chunk}
          </span>
        );
      }

      wordIndex.current++;

      return (
        <motion.span
          key={`w-${wordIndex.current}`}
          style={{whiteSpace: 'pre-wrap'}}
          variants={wordVariants}
        >
          {chunk}
        </motion.span>
      );
    });
  }

  if (node.nodeType === ELEMENT_NODE) {
    const Tag = node.tagName.toLowerCase();

    return React.createElement(
      Tag,
      {},
      Array.from(node.childNodes).map((child, i) => (
        <React.Fragment key={i}>
          {processHighlightNode(child, wordVariants, wordIndex)}
        </React.Fragment>
      ))
    );
  }

  return null;
}

function renderFadeUp({
  html,
  className,
  delay,
  trigger,
  animate: customAnimate,
  initial: customInitial,
  transition: customTransition,
  style: customStyle,
  onAnimationStart,
  onAnimationComplete,
}) {
  const children = html ? jsxParser.parse(html) : null;

  const mergedAnimation = merge(
    {},
    {initial: fadeUpConfig.initial, animate: fadeUpConfig.animate},
    (customInitial || customAnimate) ? {initial: customInitial, animate: customAnimate} : {}
  );

  const mergedTransition = merge(
    {},
    fadeUpConfig.transition,
    customTransition ? {...customTransition, delay: delay ?? customTransition.delay} : {delay}
  );

  const mergedStyle = merge({}, fadeUpConfig.style, customStyle || {});

  return (
    <motion.div
      animate={trigger ? mergedAnimation.animate : mergedAnimation.initial}
      className={className}
      initial={mergedAnimation.initial}
      style={mergedStyle}
      transition={mergedTransition}
      onAnimationComplete={onAnimationComplete}
      onAnimationStart={onAnimationStart}
    >
      {children}
    </motion.div>
  );
}

function renderHighlight({
  html,
  staggerDelay,
  delay,
  trigger,
  containerVariants: customContainerVariants,
  wordVariants: customWordVariants,
  style: customStyle,
  onAnimationStart,
  onAnimationComplete,
  theme,
}) {
  if (!isBrowser) {
    const children = html ? jsxParser.parse(html) : null;

    return <div style={customStyle}>{children}</div>;
  }

  const presetConfig = createHighlightConfig(theme, staggerDelay, delay);
  const doc = parseHTML(html);

  if (!doc || !doc.body) {
    const children = html ? jsxParser.parse(html) : null;

    return <div style={customStyle}>{children}</div>;
  }

  const wordIndex = {current: 0};
  const mergedContainerVariants = merge({}, presetConfig.containerVariants, customContainerVariants || {});
  const mergedWordVariants = merge({}, presetConfig.wordVariants, customWordVariants || {});

  return (
    <motion.div
      animate={trigger ? 'animate' : 'initial'}
      initial="initial"
      style={customStyle}
      variants={mergedContainerVariants}
      onAnimationComplete={onAnimationComplete}
      onAnimationStart={onAnimationStart}
    >
      {Array.from(doc.body.childNodes).map((node, i) => (
        <React.Fragment key={i}>
          {processHighlightNode(node, mergedWordVariants, wordIndex)}
        </React.Fragment>
      ))}
    </motion.div>
  );
}

/**
 * AnimatedText Component
 * Variants:
 * - 'fade-up-text': Fades entire content in + moves upward (whole block)
 * - 'highlight-rich-text': Word-by-word color highlight animation
 *
 * @param {Object} props - Component props
 * @param {string} props.html - HTML string to render
 * @param {VariantType} [props.variant='fade-up-text'] - Animation variant
 * @param {boolean} [props.trigger=true] - Whether to trigger animation
 * @param {string} [props.className] - CSS class name (fade-up variant only)
 * @param {number} [props.delay=0] - Delay before animation starts
 * @param {number} [props.staggerDelay=0.15] - Delay between words (highlight variant only)
 * @param {Object} [props.animate] - Custom animate values (fade-up variant)
 * @param {Object} [props.initial] - Custom initial values (fade-up variant)
 * @param {Object} [props.transition] - Custom transition (fade-up variant)
 * @param {Object} [props.style] - Custom style
 * @param {Object} [props.containerVariants] - Custom container variants (highlight variant)
 * @param {Object} [props.wordVariants] - Custom word variants (highlight variant)
 * @param {() => void} [props.onAnimationStart] - Callback when animation starts
 * @param {() => void} [props.onAnimationComplete] - Callback when animation completes
 * @returns {React.ReactElement}
 */
function AnimatedText({
  html,
  variant = 'fade-up-text',
  trigger = true,
  className = '',
  delay = 0,
  staggerDelay = 0.15,
  animate,
  initial,
  transition,
  style,
  containerVariants,
  wordVariants,
  onAnimationStart,
  onAnimationComplete,
}) {
  const theme = useTheme();

  switch (variant) {
    case 'fade-up-text':
      return renderFadeUp({
        html,
        className,
        delay,
        trigger,
        animate,
        initial,
        transition,
        style,
        onAnimationStart,
        onAnimationComplete,
      });
    case 'highlight-rich-text':
      return renderHighlight({
        html,
        staggerDelay,
        delay,
        trigger,
        containerVariants,
        wordVariants,
        style,
        onAnimationStart,
        onAnimationComplete,
        theme,
      });
    default:
      return null;
  }
}

AnimatedText.displayName = 'AnimatedText';
AnimatedText.propTypes = {
  html: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['fade-up-text', 'highlight-rich-text']),
  trigger: PropTypes.bool,
  className: PropTypes.string,
  delay: PropTypes.number,
  staggerDelay: PropTypes.number,
  animate: PropTypes.object,
  initial: PropTypes.object,
  transition: PropTypes.object,
  style: PropTypes.object,
  containerVariants: PropTypes.object,
  wordVariants: PropTypes.object,
  onAnimationStart: PropTypes.func,
  onAnimationComplete: PropTypes.func,
};

export default AnimatedText;
